import dayjs from "dayjs";
import { convertToPlain, obtenerNombreDia } from "../../utils/funcionalidadesStrings";

/**
 * procesarSeleccionFiltros
 * Lógica de filtrado de prestadores/especialidades/sedes para TurnosPaciente
 */
export const procesarSeleccionFiltros = (value, select, prestadores, usuario) => {
  let especialidadSegunSede = -1;
  const edad = dayjs().diff(usuario.nacimiento, "year");

  switch (select) {
    case "Sede":
      switch (value) {
        case 1:
          especialidadSegunSede = edad < 17 ? 107 : 98;
          break;
        case 2:
          especialidadSegunSede = edad < 17 ? 108 : 105;
          break;
        case 3:
          especialidadSegunSede = edad < 17 ? 109 : 106;
          break;
        default:
          throw new Error("Selección inválida");
      }

      const prestadoresFiltrados = especialidadSegunSede !== -1 
        ? prestadores.filter(prestador =>
            prestador.especialidad === especialidadSegunSede ||
            prestador.especialidad2 === especialidadSegunSede ||
            prestador.especialidad3 === especialidadSegunSede
          )
        : prestadores;

      return {
        listadoPrestadores: prestadoresFiltrados,
        prestadorSeleccionado: -1,
        sedeSeleccionada: value,
        especialidadSeleccionada: especialidadSegunSede
      };

    case "Especialidad":
      if (value !== -1) {
        switch (value) {
          case 98:
          case 107:
            especialidadSegunSede = 1;
            break;
          case 105:
          case 108:
            especialidadSegunSede = 2;
            break;
          case 106:
          case 109:
            especialidadSegunSede = 3;
            break;
          default:
            throw new Error("Selección inválida");
        }

        const prestadoresFiltrados = prestadores.filter(prestador => 
          prestador.especialidad === value
        );

        return {
          listadoPrestadores: prestadoresFiltrados,
          prestadorSeleccionado: -1,
          sedeSeleccionada: especialidadSegunSede,
          especialidadSeleccionada: value
        };
      } else {
        return {
          listadoPrestadores: prestadores,
          prestadorSeleccionado: -1,
          sedeSeleccionada: especialidadSegunSede,
          especialidadSeleccionada: value
        };
      }

    case "Prestador":
      const prestadorEncontrado = prestadores.find(prestador => prestador.codigo === value);
      if (prestadorEncontrado) {
        return {
          prestadorSeleccionado: value,
          especialidadSeleccionada: prestadorEncontrado.especialidad
        };
      }
      return { prestadorSeleccionado: value };

    default:
      throw new Error("Selección inválida");
  }
};

/**
 * prepararDatosComprobante
 * Arma el objeto de datos para el comprobante de turno
 */
export const prepararDatosComprobante = (turno, usuario, servicios) => {
  // Buscar domicilio del prestador si está disponible
  let domicilioPrestador = null;
  if (turno.prestador && turno.prestador.domicilioTurno) {
    domicilioPrestador = turno.prestador.domicilioTurno;
  } else if (turno.domicilioTurno) {
    domicilioPrestador = turno.domicilioTurno;
  }
  // Buscar dirección del servicio si corresponde
  let direccionServicio = (servicios && servicios.length > 0)
    ? servicios.find(s => s.codigo === turno.servicio)?.direccion || servicios[0]?.direccion
    : "";

  return {
    codigo: turno.codigo,
    equipoNom: turno.equipoNom,
    especialidadNom: turno.especialidadNom,
    estudioNom: turno.estudioNom,
    fecha: turno.fecha,
    hora: turno.hora,
    mutualNom: turno.mutualNom,
    prestadorNom: turno.prestadorNom,
    servicio: turno.servicio,
    apellido: usuario.apellido,
    nombre: usuario.nombre,
    mutualNombre: usuario.mutualNombre,
    mutualAfiliado: usuario.mutualAfiliado,
    hc: usuario.hc,
    documentoTipoNombre: usuario.documentoTipoNombre,
    documentoNro: usuario.documentoNro,
    celular: usuario.celular,
    email: usuario.email,
    lugarAtencion: domicilioPrestador || direccionServicio || "",
    preparacionEstudio: null,
  };
};

/**
 * filtrarTurnos
 * Filtra la lista de turnos según los filtros seleccionados por el usuario.
 * No modifica el array original.
 */
export const filtrarTurnos = (listadoTurnos, filtros) => {
  const {
    especialidadSeleccionada,
    prestadorSeleccionado,
    filtroAsistidos,
    filtroCancelados,
    estadoTurnosFiltrados
  } = filtros;

  let listado = listadoTurnos || [];

  if (listado.length > 0) {
    // Filtro por especialidad
    if (especialidadSeleccionada !== -1) {
      listado = listado.filter(turno => especialidadSeleccionada === turno.especialidad);
    }

    // Filtro por prestador
    if (prestadorSeleccionado !== -1) {
      listado = listado.filter(turno => prestadorSeleccionado === turno.prestadorCod);
    }

    // Filtro por asistidos
    if (filtroAsistidos !== -1) {
      listado = listado.filter(turno => filtroAsistidos === turno.asistio);
    }

    // Filtro por cancelados
    if (filtroCancelados === true) {
      // Solo turnos cancelados
      listado = listado.filter(turno => turno.aCancelar === false && turno.asistio === false);
    } else if (filtroCancelados === false) {
      // Solo turnos NO cancelados
      listado = listado.filter(turno => turno.aCancelar === true || turno.asistio === true);
    }

    // Filtro por estado
    switch (estadoTurnosFiltrados) {
      case 0: // Asistidos
        listado = listado.filter(turno => turno.asistio === true);
        break;
      case 1: // Pendientes
        listado = listado.filter(turno => turno.aCancelar === true && turno.asistio === false);
        break;
      case 2: // Cancelados
        listado = listado.filter(turno => turno.aCancelar === false && turno.asistio === false);
        break;
      default:
        // Mostrar todos
        break;
    }
  }

  return listado;
}

/**
 * obtenerEstadoTurno
 * Devuelve el estado textual de un turno: "Asistido", "Pendiente" o "Cancelado".
 */
export const obtenerEstadoTurno = (turno) => {
  if (turno.asistio) {
    return "Asistido";
  } else if (turno.aCancelar) {
    return "Pendiente";
  } else {
    return "Cancelado";
  }
};

/**
 * esTurnoFuturo
 * Determina si un turno es futuro respecto a la fecha y hora actual.
 */
export const esTurnoFuturo = (turno) => {
  const hoy = dayjs().format("YYYY-MM-DD");
  const fechaTurno = dayjs(turno.fecha).format("YYYY-MM-DD");
  const horaActual = dayjs().format("HH:mm");
  const horaTurno = dayjs(turno.hora).format("HH:mm");

  return (
    hoy !== fechaTurno ||
    (hoy === fechaTurno && horaTurno > horaActual)
  );
};

/**
 * puedeSerCancelado
 * Indica si un turno puede ser cancelado por el paciente.
 */
export const puedeSerCancelado = (turno) => {
  return esTurnoFuturo(turno) && turno.aCancelar && !turno.asistio;
};

/**
 * puedeReservarNuevoTurno
 * Indica si el paciente puede reservar un nuevo turno según el estado del actual.
 */
export const puedeReservarNuevoTurno = (turno) => {
  return turno.aCancelar; // Solo si no está cancelado
};

/**
 * obtenerPreparacionEstudio
 * Devuelve la preparación asociada a un estudio si corresponde.
 */
export const obtenerPreparacionEstudio = (turno, estudios) => {
  if (turno.prestadorNom === "EQUIPOS") {
    const estudio = estudios.find(e => e.codigo === turno.estudio);
    if (estudio && estudio.preparacion !== "") {
      return convertToPlain(estudio.preparacion);
    }
  }
  return null;
};

/**
 * formatearFechaTurno
 * Devuelve una cadena legible con la fecha y hora del turno.
 */
export const formatearFechaTurno = (fecha, hora) => {
  return `${obtenerNombreDia(
    dayjs(fecha).format("dddd")
  )} ${dayjs(fecha).format("DD")} de ${dayjs(
    fecha
  ).format("MMMM, YYYY")} a las ${dayjs(hora).format("HH:mm")}`;
};

/**
 * sedesDisponibles
 * Listado estático de sedes disponibles para turnos.
 */
export const sedesDisponibles = [
  { codigo: 1, text: "Central – Humberto Primo 546, Córdoba" },
  { codigo: 2, text: "Alta Córdoba – Juan Sarachaga 768, Córdoba" },
  { codigo: 3, text: "Villa Allende – Rio de Janeiro 137 Piso 2, Villa Allende" },
];

/**
 * opcionesFiltros
 * Opciones de filtros para turnos (cancelados, asistidos, estados).
 */
export const opcionesFiltros = {
  cancelados: [
    { codigo: true, text: "Cancelados" },
    { codigo: false, text: "No cancelados" },
  ],
  asistidos: [
    { codigo: false, text: "No asistidos" },
    { codigo: true, text: "Asistidos" },
  ],
  estados: [
    { codigo: 0, text: "Asistidos" },
    { codigo: 1, text: "Pendientes" },
    { codigo: 2, text: "Cancelados" },
  ]
};

/**
 * validarDatosTurno
 * Valida la estructura mínima de un objeto turno.
 */
export const validarDatosTurno = (turno) => {
  return {
    tieneId: Boolean(turno?.codigo),
    tienePrestador: Boolean(turno?.prestadorNom),
    tieneFecha: Boolean(turno?.fecha),
    tieneHora: Boolean(turno?.hora),
    esValido: Boolean(turno?.codigo && turno?.prestadorNom && turno?.fecha && turno?.hora)
  };
};
